<template>
	<section>
		<!-- Meta tags -->
		<Title>{{ $t("seo.pages.jobs.title") }}</Title>
		<Meta name="description" :content="$t('seo.pages.jobs.description')" />
		<Link rel="canonical" :href="`https://${globalConfig.host}${$route.fullPath}`" />
		<Link v-if="globalConfig.isBlue" rel="icon" type="image/x-icon" href="/blue-favicon.ico?v3" />
		<Link v-else rel="icon" type="image/x-icon" href="/favicon.ico?v3" />
		<Meta property="og:url" :content="`https://${globalConfig.host}${$route.fullPath}`" />
		<Meta property="og:type" content="website" />
		<Meta property="og:title" :content="$t('seo.pages.jobs.title')" />
		<Meta property="og:description" :content="$t('seo.pages.jobs.description')" />
		<Meta
			property="og:locale"
			:content="`${$i18n.locale}_${$i18n.locale == 'en' ? 'US' : $i18n.locale.toUpperCase()}`"
		/>
		<Meta
			property="og:image"
			content="https://res.cloudinary.com/djbs/image/upload/v1669288084/content/DreamJobs/Graphics/ogimage_dreamjobs.png"
		/>
		<Meta property="og:image:type" content="image/jpeg" />
		<Meta property="og:image:width" content="1200" />
		<Meta property="og:image:height" content="628" />

		<HeroSubpage
			type="jobs"
			:title="$t('jobhero.title')"
			:subtitle="$t('jobhero.subtitle')"
			:searchData="{ paramValues: paramValues, listLength: totalResults }"
		/>
		<div class="flex flex-col items-center w-full">
			<SearchbarJobs ref="searchbarJobsRef" @run-search="onSearch" @fields-data="getFieldsData" />
		</div>
		<div id="job-results">
			<SectionsSzmdCtaBanner v-if="szmdVotingInProgress && !szmdCtaBannerHide" />
			<BannerContainer banner-type="frontend-jobs-banner" class="banner-section" v-slot="{ content }">
				<section
					class="container relative flex flex-col md:flex-row justify-center mx-5 sm:mx-8 md:mx-auto my-8"
					v-html="content"
				></section>
			</BannerContainer>

			<!-- Carousels & further jobs -->
			<template v-if="!searching && !searchingFromCarousel">
				<!-- Recommended / highlighted jobs carousel -->
				<template v-if="isAuth">
					<JobsPageCarousel
						:title="$t('jobs.recommended')"
						:carousel-data-url="recommendedCarouselUrl"
						:data-url="recommendedDataUrl"
						@data-fetched="processApiData"
						hasGradientBg
						:route-param="carouselParams[0]"
						carouselType="offered-jobs"
					/>
				</template>
				<template v-else>
					<JobsPageCarousel
						:title="$t('jobs.highlighted')"
						:highlighted-gold="true"
						:data-url="highlightedDataUrl"
						@data-fetched="processApiData"
						hasGradientBg
						:route-param="carouselParams[1]"
						carouselType="higlighted-company-jobs"
					/>
				</template>

				<SectionsDailyFavoriteJobs />

				<!-- Further jobs list -->
				<JobsPageList
					:title="$t('jobs.furtherJobs')"
					show-results-number
					:url="furtherJobsUrl"
					@set-page="setPage"
				/>
			</template>

			<!-- Search results from carusel -->
			<template v-else-if="searchingFromCarousel">
				<JobsPageList
					:title="$t(`jobs.${searchingFromCarouselType}`)"
					:url="apiUrlFromCarousel"
					@set-page="setPage"
				/>
			</template>

			<!-- Search results -->
			<template v-else>
				<JobsPageList
					:title="$t('jobs.results')"
					:url="href"
					@get-results-number="totalResults = $event"
					@set-page="setPage"
				/>
			</template>
		</div>
	</section>
</template>

<script lang="ts">
import { computed, defineComponent, nextTick, ref, watch } from "vue";
import { constructApiUrl } from "@@/composables/helpers";

export default defineComponent({
	name: "JobsPage",
	setup() {
		const isAuth = useState("isAuth");
		const { locale } = useI18n();
		const { szmdVotingInProgress, szmdCtaBannerHide } = useSzmdConfig();

		const route = useRoute();
		const router = useRouter();
		const searchbarJobsRef = ref();

		const carouselParams = ["allRecommendedJobs", "highlightedJobs"];

		const paramValues = ref("");
		const totalResults = ref(0);

		// ── Init URLs ─────────────────────────────────────────────────────
		const recommendedDataUrl = ref(globalConfig.API);
		const highlightedDataUrl = ref(globalConfig.API + "/jobs?scope[]=highlightedGold");
		const recommendedCarouselUrl = ref("jobs?");
		const furtherJobsUrl = ref(globalConfig.API + "/jobs");

		// ── Computed state: derived from route.query (like companies page) ─
		const searchingFromCarousel = computed(() => carouselParams.some((p) => Boolean(route.query[p])));

		const searchingFromCarouselType = computed(() => {
			if (route.query[carouselParams[0]]) return "recommended";
			if (route.query[carouselParams[1]]) return "highlighted";
			return "";
		});

		const searching = computed(() => Object.keys(route.query).length > 0 && !searchingFromCarousel.value);

		const apiUrlFromCarousel = computed(() => {
			if (route.query[carouselParams[0]]) return recommendedDataUrl.value;
			if (route.query[carouselParams[1]]) return highlightedDataUrl.value;
			return "";
		});

		function buildJobsSearchUrl() {
			const searchUrl = constructApiUrl("/jobs");
			searchUrl.searchParams.append("region", useRegion.getCurrentRegion().code.toLowerCase());
			const queryParams = route.query;
			searchUrl.searchParams.append("page", queryParams.page ? String(queryParams.page) : "1");
			const keywordParam = queryParams.keyword || queryParams.query;
			if (keywordParam) searchUrl.searchParams.append("keyword", keywordParam.toString());
			if (queryParams.category)
				addArrayToParams("tags[job-category][]", queryParams.category.toString().split(","), searchUrl);
			if (queryParams.subcategory)
				addArrayToParams("tags[job-subcategory][]", queryParams.subcategory.toString().split(","), searchUrl);
			if (queryParams.location)
				addArrayToParams("tags[office-location][]", queryParams.location.toString().split(","), searchUrl);
			if (queryParams.type)
				addArrayToParams("tags[job-type][]", queryParams.type.toString().split(","), searchUrl);
			if (queryParams.extra) addArrayToParams("badges[]", queryParams.extra.toString().split(","), searchUrl);
			if (queryParams.level)
				addArrayToParams("tags[job-level][]", queryParams.level.toString().split(","), searchUrl);
			if (queryParams.salary) searchUrl.searchParams.append("scope[]", "hasVisibleSalary");
			if (queryParams.new_job) searchUrl.searchParams.append("scope[]", "latestJobs");
			if (queryParams.expiring) searchUrl.searchParams.append("scope[]", "expiring");
			if (globalConfig.isBlue) searchUrl.searchParams.append("scope[]", "isBlue");
			if (globalConfig.isNotBlueHU) searchUrl.searchParams.append("scope[]", "isNotBlue");
			return searchUrl.href;
		}

		const href = computed(() => (searching.value ? buildJobsSearchUrl() : ""));

		// ── Route watcher (like companies page) ───────────────────────────
		watch(
			() => route.query,
			(newQuery) => {
				if (Object.keys(newQuery).length === 0) {
					paramValues.value = "";
					totalResults.value = 0;
					// Reset searchbar fields
					searchbarJobsRef.value?.resetSearch?.();
				}
				if (process.client && Object.keys(newQuery).length > 0) {
					nextTick(() => {
						// Keep hero summary in sync even for Enter/navigation-driven searches
						searchbarJobsRef.value?.emitFieldsData?.();
						const target = document.getElementById("job-results");
						if (target) window.scrollTo({ top: target.offsetTop - 76, behavior: "smooth" });
					});
				}
			},
			{ immediate: true }
		);

		function setPage(page: number) {
			router.push({
				path: `/${locale.value}/jobs`,
				query: {
					...route.query,
					page: page > 1 ? page : undefined,
				},
			});
		}

		function buildRecommendedUrl() {
			//  Always add the region and the jobsForYou scope first!
			recommendedCarouselUrl.value += `region=${useRegion.getCurrentRegion().code.toLowerCase()}`;
			recommendedCarouselUrl.value += "&scope[]=jobsForYou";

			// Add global config scopes
			if (globalConfig.isBlue) {
				recommendedCarouselUrl.value += "&scope[]=isBlue";
			}
			if (globalConfig.isNotBlueHU) {
				recommendedCarouselUrl.value += "&scope[]=isNotBlue";
			}

			// Check for user tags
			const user = useAuth.getUser();
			const userTags = user?.tags;

			if (userTags) {
				// categories
				if (userTags.job_category) {
					userTags.job_category.data?.forEach((cat) => {
						recommendedCarouselUrl.value += `&tags[job-category][]=${cat.id}`;
					});
				}
				// subcategories
				if (userTags.job_subcategory) {
					userTags.job_subcategory.data?.forEach((cat) => {
						recommendedCarouselUrl.value += `&tags[job-subcategory][]=${cat.id}`;
					});
				}
				// job levels
				if (userTags.job_level) {
					userTags.job_level.data?.forEach((level) => {
						recommendedCarouselUrl.value += `&tags[job-level][]=${level.id}`;
					});
				}
			}

			// Finalize the data url
			recommendedDataUrl.value += `/${recommendedCarouselUrl.value}`;
		}

		function processApiData(event: { url: string; param: string }) {
			const query: Record<string, string> = {};
			query[event.param] = "true";
			router.push({
				path: `/${locale.value}/jobs`,
				query: query,
			});
		}

		function getFieldsData(event: string[]) {
			paramValues.value = event.join(", ");
		}

		function onSearch() {
			// Search state is computed from route.query; searchbar already navigated before emitting this event
		}

		// ── Init ──────────────────────────────────────────────────────────
		const regionCode = useRegion.getCurrentRegion().code.toLowerCase();
		highlightedDataUrl.value += `&region=${regionCode}`;
		furtherJobsUrl.value += `?region=${regionCode}`;
		if (globalConfig.isBlue) {
			furtherJobsUrl.value += "&scope[]=isBlue";
			highlightedDataUrl.value += "&scope[]=isBlue";
		}
		if (globalConfig.isNotBlueHU) {
			furtherJobsUrl.value += "&scope[]=isNotBlue";
			highlightedDataUrl.value += "&scope[]=isNotBlue";
		}
		// Watch for the user to be authenticated, then build the URL
		watch(
			isAuth,
			(authenticated) => {
				if (authenticated && recommendedCarouselUrl.value === "jobs?") {
					buildRecommendedUrl();
				}
			},
			{ immediate: true }
		);

		return {
			isAuth,
			recommendedCarouselUrl,
			recommendedDataUrl,
			highlightedDataUrl,
			processApiData,
			apiUrlFromCarousel,
			furtherJobsUrl,
			onSearch,
			searching,
			searchingFromCarousel,
			searchingFromCarouselType,
			href,
			carouselParams,
			getFieldsData,
			paramValues,
			totalResults,
			searchbarJobsRef,
			setPage,
			szmdVotingInProgress,
			szmdCtaBannerHide,
		};
	},
});
</script>
